import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const TollsTab = ({ api, tolls = [], fetchTolls, currencySymbol = '$' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingToll, setEditingToll] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', account_id: '', is_active: true });

  const resetForm = () => {
    setForm({ name: '', price: '', account_id: '', is_active: true });
    setEditingToll(null);
  };

  const handleOpenDialog = (toll = null) => {
    if (toll) {
      setEditingToll(toll);
      setForm({
        name: toll.name,
        price: toll.price ?? '',
        account_id: toll.account_id || '',
        is_active: toll.is_active,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      price: parseFloat(form.price) || 0,
      account_id: form.account_id.trim() || null,
      is_active: form.is_active,
    };
    try {
      if (editingToll) {
        await api.put(`/tolls/${editingToll.id}`, payload);
        toast.success('Toll updated');
      } else {
        await api.post('/tolls', payload);
        toast.success('Toll created');
      }
      setDialogOpen(false);
      resetForm();
      fetchTolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = (toll) => {
    setDeleteTarget(toll);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/tolls/${deleteTarget.id}`);
      toast.success('Toll deleted');
      fetchTolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete toll');
    } finally {
      setDeleteTarget(null);
    }
  };

  const toggleActive = async (toll) => {
    try {
      await api.put(`/tolls/${toll.id}`, { is_active: !toll.is_active });
      toast.success(`Toll ${toll.is_active ? 'deactivated' : 'activated'}`);
      fetchTolls();
    } catch (error) {
      toast.error('Failed to update toll');
    }
  };

  const filteredTolls = tolls.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.account_id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Tolls</CardTitle>
              <CardDescription>
                Manage tolls that can be added to dockets (e.g., CityLink, Gateway Bridge). Each toll has a price and an income account.
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-toll-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Toll
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="toll-search"
              placeholder="Search tolls..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Account ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTolls.map(toll => (
                <TableRow key={toll.id} data-testid={`toll-row-${toll.id}`}>
                  <TableCell className="font-medium">{toll.name}</TableCell>
                  <TableCell>{currencySymbol}{(toll.price ?? 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {toll.account_id ? <Badge variant="outline">{toll.account_id}</Badge> : <span className="text-muted-foreground text-sm">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={toll.is_active ? 'default' : 'secondary'}
                      className={toll.is_active ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : ''}
                    >
                      {toll.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`toll-actions-${toll.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(toll)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(toll)}>
                          {toll.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(toll)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTolls.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No matching tolls found' : 'No tolls configured. Add your first one!'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingToll ? 'Edit Toll' : 'Add Toll'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="toll-form">
            <div>
              <Label htmlFor="toll-name">Name</Label>
              <Input
                id="toll-name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., CityLink Toll, Gateway Bridge"
                data-testid="toll-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="toll-price">Price ({currencySymbol})</Label>
              <Input
                id="toll-price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                data-testid="toll-price-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="toll-account">Account ID</Label>
              <Input
                id="toll-account"
                value={form.account_id}
                onChange={(e) => setForm(prev => ({ ...prev, account_id: e.target.value }))}
                placeholder="Income account code (e.g., 200)"
                data-testid="toll-account-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="toll-active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="toll-active">Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="toll-submit-btn">
                {editingToll ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-toll-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Toll
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the toll "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-toll-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-toll-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TollsTab;
